import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { CheckCircle as CheckCircleIcon, Cancel as CancelIcon, Close as CloseIcon, Info as InfoIcon, Warning as WarningIcon } from "@mui/icons-material";
import { format } from "date-fns";

const CATEGORY_LABELS = {
  ANNUAL_LEAVE: "Annual Leave",
  BUSINESS_TRIP: "Business Trip",
  NATIONAL_HOLIDAY: "National Holiday",
  COMPENSATION: "Compensation",
  OTHER: "Other",
};

// Extract HH:mm from a datetime string without timezone conversion
const extractTime = (dateTimeStr) => {
  if (!dateTimeStr) return "";
  const str = String(dateTimeStr);
  const match = str.match(/T(\d{2}:\d{2})/);
  if (match) return match[1];
  const timeOnly = str.match(/^(\d{2}:\d{2})/);
  if (timeOnly) return timeOnly[1];
  return "";
};

// --- Inspect Entry Dialog ---
export const InspectDialog = ({ open, entry, date, onClose, isAdminOrManager, onApprove, onDisapprove }) => {
  const [showDeclineInput, setShowDeclineInput] = useState(false);
  const [declineNote, setDeclineNote] = useState("");

  const handleClose = () => {
    setShowDeclineInput(false);
    setDeclineNote("");
    onClose();
  };

  const handleApprove = () => {
    onApprove(entry.id);
    handleClose();
  };

  const handleConfirmDecline = () => {
    onDisapprove(entry.id, declineNote);
    setShowDeclineInput(false);
    setDeclineNote("");
    handleClose();
  };

  const canApproveOrDecline =
    isAdminOrManager && entry?.entryType === "ACTUAL" && entry?.approvalStatus === "WAITING_FOR_APPROVAL";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <InfoIcon sx={{ color: "#1565c0" }} />
        Entry Details
        {date && (
          <Typography variant="subtitle2" color="textSecondary" sx={{ ml: 1 }}>
            {format(date, "dd MMMM yyyy, EEEE")}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {entry && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Grid container spacing={2}>
              {/* Flights — iterate entry.flights[], fall back to flat fields */}
              {(entry.flights && entry.flights.length > 0
                ? entry.flights
                : [{ destination: entry.destination, flight: entry.flight, etd: entry.etd, eta: entry.eta }]
              ).map((f, i, arr) => (
                <React.Fragment key={i}>
                  {arr.length > 1 && (
                    <Grid item xs={12}>
                      <Typography variant="caption" sx={{ fontWeight: "bold", color: "#1565c0" }}>
                        Flight {i + 1}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Destination</Typography>
                    <Typography variant="body1">{f.destination || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">Flight</Typography>
                    <Typography variant="body1">{f.flight || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">ETD</Typography>
                    <Typography variant="body1">{extractTime(f.etd) || "—"}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">ETA</Typography>
                    <Typography variant="body1">{extractTime(f.eta) || "—"}</Typography>
                  </Grid>
                </React.Fragment>
              ))}
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Comment</Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {entry.comment || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="textSecondary">Others</Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>
                  {entry.others || "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">in EU</Typography>
                <Typography variant="body1">{entry.inEu ? "Yes" : "No"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">in Sin</Typography>
                <Typography variant="body1">{entry.inSin ? "Yes" : "No"}</Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="textSecondary">Type</Typography>
                <Chip
                  label={entry.entryType === "TEMPLATE" ? "Template" : "Actual"}
                  size="small"
                  variant="outlined"
                  sx={{
                    mt: 0.5,
                    borderColor: entry.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                    color: entry.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                  }}
                />
              </Grid>
              {entry.category && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Category</Typography>
                  <Typography variant="body1">
                    {{
                      ANNUAL_LEAVE: "Annual Leave",
                      BUSINESS_TRIP: "Business Trip",
                      NATIONAL_HOLIDAY: "National Holiday",
                      COMPENSATION: "Compensation",
                      OTHER: "Other",
                    }[entry.category] || entry.category}
                  </Typography>
                </Grid>
              )}
              {entry.specialOccasion && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">Special Occasion</Typography>
                  <Typography variant="body1" sx={{ color: "#6a1b9a", fontStyle: "italic" }}>
                    {entry.specialOccasion}
                    {entry.sharedSpecialOccasion && " (Shared)"}
                  </Typography>
                </Grid>
              )}
            </Grid>

            {/* Approval Status Section — only for ACTUAL entries */}
            {entry.entryType === "ACTUAL" && (
            <Box
              sx={{
                mt: 1,
                p: 2,
                borderRadius: 2,
                backgroundColor:
                  entry.approvalStatus === "APPROVED"
                    ? "#e8f5e9"
                    : entry.approvalStatus === "NOT_APPROVED"
                    ? "#ffebee"
                    : "#fff8e1",
                border: "1px solid",
                borderColor:
                  entry.approvalStatus === "APPROVED"
                    ? "#a5d6a7"
                    : entry.approvalStatus === "NOT_APPROVED"
                    ? "#ef9a9a"
                    : "#ffe082",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Approval Status
                </Typography>
                {canApproveOrDecline && (
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={<CheckCircleIcon fontSize="small" />}
                      onClick={handleApprove}
                      sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.25 }}
                    >
                      Approve
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<CancelIcon fontSize="small" />}
                      onClick={() => setShowDeclineInput((v) => !v)}
                      sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.25 }}
                    >
                      Decline
                    </Button>
                  </Box>
                )}
              </Box>
              <Chip
                label={
                  entry.approvalStatus === "APPROVED"
                    ? "Approved"
                    : entry.approvalStatus === "NOT_APPROVED"
                    ? "Not Approved"
                    : "Waiting for Approval"
                }
                size="small"
                sx={{
                  fontWeight: "bold",
                  backgroundColor:
                    entry.approvalStatus === "APPROVED"
                      ? "#c8e6c9"
                      : entry.approvalStatus === "NOT_APPROVED"
                      ? "#ffcdd2"
                      : "#fff9c4",
                  color:
                    entry.approvalStatus === "APPROVED"
                      ? "#2e7d32"
                      : entry.approvalStatus === "NOT_APPROVED"
                      ? "#c62828"
                      : "#f57f17",
                }}
              />
              {entry.approvalStatus === "NOT_APPROVED" && (
                <Box sx={{ mt: 1.5 }}>
                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: "bold" }}>
                    Disapproval Reason
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.5,
                      p: 1.5,
                      borderRadius: 1,
                      backgroundColor: "#fff",
                      border: "1px solid #ef9a9a",
                      color: entry.disapprovalNote ? "#c62828" : "#999",
                      whiteSpace: "pre-wrap",
                      fontStyle: entry.disapprovalNote ? "normal" : "italic",
                    }}
                  >
                    {entry.disapprovalNote || "No reason provided"}
                  </Typography>
                </Box>
              )}

              {/* Inline decline note input */}
              <Collapse in={showDeclineInput}>
                <Box sx={{ mt: 1.5 }}>
                  <TextField
                    label="Decline reason (optional)"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={declineNote}
                    onChange={(e) => setDeclineNote(e.target.value)}
                    placeholder="e.g. Missing flight details..."
                    autoFocus
                  />
                  <Box sx={{ display: "flex", gap: 1, mt: 1, justifyContent: "flex-end" }}>
                    <Button size="small" onClick={() => { setShowDeclineInput(false); setDeclineNote(""); }}>
                      Cancel
                    </Button>
                    <Button size="small" variant="contained" color="error" onClick={handleConfirmDecline}>
                      Confirm Decline
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Disapprove single entry dialog ---
export const DisapproveDialog = ({ open, note, onNoteChange, onConfirm, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "#c62828" }}>Disapprove Entry</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
          Please provide a reason for disapproving this entry (optional).
        </Typography>
        <TextField
          label="Disapproval Note"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="e.g. Missing flight details, dates conflict..."
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm}>
          Disapprove
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Bulk Disapprove dialog ---
export const BulkDisapproveDialog = ({ open, count, note, onNoteChange, onConfirm, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: "#c62828" }}>
        Disapprove {count} Entries
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
          This will disapprove {count} selected entries. Provide an optional note.
        </Typography>
        <TextField
          label="Disapproval Note"
          size="small"
          fullWidth
          multiline
          rows={3}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          placeholder="e.g. Missing details, schedule conflict..."
          autoFocus
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" onClick={onConfirm}>
          Disapprove ({count})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Conflict Preview Dialog ---
// Shows other users' existing entries that overlap with the date(s) being added.
export const ConflictPreviewDialog = ({ open, conflictingEntries, onProceed, onGoBack }) => {
  // Group entries by date for a cleaner display
  const byDate = {};
  (conflictingEntries || []).forEach((e) => {
    const dateKey = e.entryDate ? e.entryDate.substring(0, 10) : "—";
    if (!byDate[dateKey]) byDate[dateKey] = [];
    byDate[dateKey].push(e);
  });
  const sortedDates = Object.keys(byDate).sort();

  return (
    <Dialog open={open} onClose={onGoBack} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <WarningIcon sx={{ color: "#f57c00" }} />
        <Box>
          <Typography variant="h6" component="span">Date Conflict</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ display: "block" }}>
            The following colleagues already have entries on the selected date(s).
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {sortedDates.length === 0 ? (
          <Typography sx={{ p: 3 }}>No conflicts found.</Typography>
        ) : (
          sortedDates.map((dateKey) => {
            const entries = byDate[dateKey];
            let formattedDate = dateKey;
            try {
              formattedDate = format(new Date(dateKey + "T00:00:00"), "dd MMMM yyyy, EEEE");
            } catch (e) {}
            return (
              <Box key={dateKey}>
                <Box sx={{ px: 2, py: 1, backgroundColor: "#fff3e0", borderBottom: "1px solid #ffe0b2" }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#e65100" }}>
                    {formattedDate}
                  </Typography>
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#fafafa" }}>
                      <TableCell sx={{ fontWeight: "bold", width: 160 }}>Person</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Destination</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Flight</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Comment</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Others</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Special Occasions</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: 90 }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: "bold", width: 160 }}>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry, idx) => {
                      const personName = entry.user
                        ? entry.user.name || entry.user.username || "—"
                        : "—";
                      return (
                        <TableRow key={idx} hover>
                          <TableCell>{personName}</TableCell>
                          <TableCell>{entry?.destination || "—"}</TableCell>
                          <TableCell>{entry?.flight || "—"}</TableCell>
                          <TableCell>{entry?.comment || "—"}</TableCell>
                          <TableCell>{entry?.others || "—"}</TableCell>
                          <TableCell>{entry?.specialOccasion || "—"}</TableCell>
                          <TableCell>
                            <Chip
                              label={entry.entryType === "TEMPLATE" ? "Template" : "Actual"}
                              size="small"
                              variant="outlined"
                              sx={{
                                borderColor: entry.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                                color: entry.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                                fontSize: "0.7rem",
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {entry.approvalStatus ? (
                              <Chip
                                label={entry.approvalStatus.replace(/_/g, " ")}
                                size="small"
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: "bold",
                                  backgroundColor:
                                    entry.approvalStatus === "APPROVED"
                                      ? "#c8e6c9"
                                      : entry.approvalStatus === "NOT_APPROVED"
                                      ? "#ffcdd2"
                                      : "#fff9c4",
                                  color:
                                    entry.approvalStatus === "APPROVED"
                                      ? "#2e7d32"
                                      : entry.approvalStatus === "NOT_APPROVED"
                                      ? "#c62828"
                                      : "#f57f17",
                                }}
                              />
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            );
          })
        )}
        <Box sx={{ px: 2, py: 1.5, backgroundColor: "#fafafa", borderTop: "1px solid #e0e0e0" }}>
          <Typography variant="body2" sx={{ color: "#666" }}>
            You can proceed to save your entry or go back to make changes.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button variant="outlined" onClick={onGoBack}>
          Go Back
        </Button>
        <Button variant="contained" color="warning" onClick={onProceed} sx={{ color: "#fff" }}>
          Proceed Anyway
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// --- Date Overview Dialog ---
// Shows all users' entries for a specific date, grouped by user.
export const DateOverviewDialog = ({ open, date, entries, loading, onClose }) => {
  const grouped = useMemo(() => {
    const map = {};
    (entries || []).forEach((e) => {
      const userId = e.user?.id || "unknown";
      if (!map[userId]) {
        map[userId] = { user: e.user, entries: [] };
      }
      map[userId].entries.push(e);
    });
    return Object.values(map).sort((a, b) => {
      const na = a.user?.name || a.user?.username || "";
      const nb = b.user?.name || b.user?.username || "";
      return na.localeCompare(nb);
    });
  }, [entries]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            All Users' Entries
          </Typography>
          {date && (
            <Typography variant="caption" color="textSecondary">
              {format(date, "dd MMMM yyyy, EEEE")}
            </Typography>
          )}
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : grouped.length === 0 ? (
          <Typography variant="body2" sx={{ color: "#999", textAlign: "center", py: 4 }}>
            No entries found for this date.
          </Typography>
        ) : (
          grouped.map(({ user, entries: userEntries }) => (
            <Box key={user?.id || "unknown"}>
              <Box
                sx={{
                  backgroundColor: "#f0f4ff",
                  px: 2,
                  py: 0.75,
                  borderBottom: "1px solid #dde3f0",
                  borderTop: "1px solid #dde3f0",
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#1E2685" }}>
                  {user?.name || user?.username || "Unknown User"}
                </Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#fafafa" }}>
                      {["Type", "Destination", "Flight", "ETD", "ETA", "Comment", "Category", "Status"].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: "bold", fontSize: "0.72rem", py: 0.5, whiteSpace: "nowrap" }}>
                          {h}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userEntries.map((e) => (
                      <TableRow key={e.id} hover>
                        <TableCell sx={{ py: 0.5 }}>
                          <Chip
                            label={e.entryType === "TEMPLATE" ? "Template" : "Actual"}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: "0.65rem",
                              height: 18,
                              borderColor: e.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                              color: e.entryType === "TEMPLATE" ? "#7b1fa2" : "#1565c0",
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 0.5 }}>{e.destination || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 0.5 }}>{e.flight || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 0.5 }}>{extractTime(e.etd) || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 0.5 }}>{extractTime(e.eta) || "—"}</TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 0.5, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Tooltip title={e.comment || ""} arrow disableHoverListener={!e.comment}>
                            <span>{e.comment || "—"}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ fontSize: "0.75rem", py: 0.5, whiteSpace: "nowrap" }}>
                          {CATEGORY_LABELS[e.category] || e.category || "—"}
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          {e.entryType === "ACTUAL" ? (
                            <Chip
                              label={
                                e.approvalStatus === "APPROVED"
                                  ? "Approved"
                                  : e.approvalStatus === "NOT_APPROVED"
                                  ? "Not Approved"
                                  : "Waiting"
                              }
                              size="small"
                              sx={{
                                fontSize: "0.65rem",
                                height: 18,
                                backgroundColor:
                                  e.approvalStatus === "APPROVED"
                                    ? "#e8f5e9"
                                    : e.approvalStatus === "NOT_APPROVED"
                                    ? "#ffebee"
                                    : "#fff8e1",
                                color:
                                  e.approvalStatus === "APPROVED"
                                    ? "#2e7d32"
                                    : e.approvalStatus === "NOT_APPROVED"
                                    ? "#c62828"
                                    : "#f57f17",
                                fontWeight: "bold",
                              }}
                            />
                          ) : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
